/**
 * Wrapper and manager for resources strings, matches in with how we do server-side translations.
 */

const T = function(...args: any[]) { return appn.translator.getString(...args); };
const TP = function(...args: any[]) { return appn.translator.getPluralString(...args); };

namespace appn.translator {
    export let resx: any;                               // populated by server
    export let RESXKEYLENGTH: number;                   //
    export let plurals: Array<PluralRule>;              //
    export let culture: string;                         //

    export function getString(...args: any[]): string {
        let template = args.shift() ?? '';

        if (appn.translator.resx) {
            let cleanKey = getResourceKeyFor(template);
            if (appn.translator.resx[cleanKey] !== undefined)
                template = appn.translator.resx[cleanKey];
            // track missing keys if test/debug
            else if (appn.isDebug || appn.isTestSuiteRequest) {
                let missing = JSON.parse(localStorage.getItem('appn.missingkeys') ?? '{}') ?? {};
                if (missing[culture] === undefined)
                    missing[culture] = [];
                if (!missing[culture].includes(template)) {
                    missing[culture].push(template);
                    localStorage.setItem('appn.missingkeys', JSON.stringify(missing));
                }
            }
        }

        if (args.length === 0)
            return template;

        return formatString(template, args);
    };

    function formatString(val: string, params: string[]): string {
        return val.replace(/{(\d+)}/g, (match, index) => {
            return typeof params[index] !== 'undefined' ? params[index] : match;
        });
    }

    export function getPluralString(...args: any[]): string {
        let zero = args.shift() as string;
        let one = args.shift() as string;

        // we init under assumption of this being the shortcut 4 param call
        let other = args.shift() as string;
        let two = other;
        let few = other;
        let many = other;

        // this must be the full 7 param call, so reinit accordingly
        if (args.length > 1) {
            few = args.shift() as string;
            many = args.shift() as string;
            other = args.shift() as string;
        }
        let number = args.shift() as number;

        if (number === 0)
            return getString(zero);

        var match = plurals.find(r => r.Min <= number && number <= r.Max);

        switch (match?.Type) {
            case CLDRType.One:
                return getString(one);
            case CLDRType.Two:
                return getString(two, number);
            case CLDRType.Few:
                return getString(few, number);
            case CLDRType.Many:
                return getString(many, number);
            default:
                return getString(other, number);
        }
    }

    // NB: This *must* match with server-side implementation.
    export function getResourceKeyFor(text: string): string {
        var normed = normaliseString(text);

        var cleaned = normed.substring(0, RESXKEYLENGTH)
            .replace(/\s+/g, '_')
            // Explicitly select anything not in unicode letters, numbers, and underscores. JS does not support all unicode characters using \W.
            .replace(/[^\p{L}\p{N}_]+/gu, '');

        return `${cleaned}_${getPersistentHashCode(normed)}`;
    }

    // NB: This *must* match with server-side implementation.
    export function normaliseString(val: string): string {
        return val.replace(/\r/g, '').replace(/[ \t]{2,}/g, ' ');
    }

    // uses 32 bit integer operations to handle int32 overflow same as C#
    export function getPersistentHashCode(str: string): number {
        let hash1 = iadd(5381 << 16, 5381);
        let hash2 = hash1;
        for (let i = 0; i < str.length; i += 2) {
            hash1 = iadd(hash1 << 5, hash1) ^ str.charCodeAt(i);
            if (i === str.length - 1) {
                break;
            }
            hash2 = iadd(hash2 << 5, hash2) ^ str.charCodeAt(i + 1);
        }
        return Math.abs(iadd(hash1, Math.imul(hash2, 1566083941)));
    }

    export function i32(value: number): number {
        return value | 0;
    }

    export function iadd(a: number, b: number): number {
        return ((a | 0) + (b | 0)) | 0;
    }

    export interface PluralRule {
        Type: CLDRType;
        Min: number;
        Max: number;
    }

    export enum CLDRType {
        One,
        Two,
        Few,
        Many,
        Other
    }
}
